import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package, Plus, Loader2, ShoppingCart, DollarSign, Users,
  Tag, BarChart3, Megaphone, ShoppingBag, Settings, Mail,
  Activity, Key, Shield, ClipboardList, Search, Lock, Moon, Sun,
  Monitor, BookOpen, X, Trophy,
  MailPlus,
} from "lucide-react";
import { toast } from "sonner";

// Admin tab components
import { ApiKeysTab } from "@/components/admin/ApiKeysTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { OverviewTab } from "@/components/admin/OverviewTab";
import { ProductsTab } from "@/components/admin/ProductsTab";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { SubAdminsTab } from "@/components/admin/SubAdminsTab";
import { ActivityLogTab } from "@/components/admin/ActivityLogTab";
import { PromoCodesTab } from "@/components/admin/PromoCodesTab";
import { BannersTab } from "@/components/admin/BannersTab";
import { ShowcaseTab } from "@/components/admin/ShowcaseTab";
import { SiteSettingsTab } from "@/components/admin/SiteSettingsTab";
import { EngagementTab } from "@/components/admin/EngagementTab";
import { SubscribersTab } from "@/components/admin/SubscribersTab";
import { SecurityTab } from "@/components/admin/SecurityTab";
import { CustomerViewTab } from "@/components/admin/CustomerViewTab";
import { EmailSettingsTab } from "@/components/admin/EmailSettingsTab";
import { SampleMocksManager } from "@/components/admin/SampleMocksManager";
import { QuizAnalytics } from "@/components/admin/QuizAnalytics";
import { QuizChallengeTab } from "@/components/admin/QuizChallengeTab";
import { SalesChart } from "@/components/admin/SalesChart";

// Dialog components
import { SubAdminDialog } from "@/components/admin/dialogs/SubAdminDialog";
import { ProductDialog } from "@/components/admin/dialogs/ProductDialog";
import { PromoCodeDialog } from "@/components/admin/dialogs/PromoCodeDialog";
import { BannerDialog } from "@/components/admin/dialogs/BannerDialog";
import { ShowcaseDialog } from "@/components/admin/dialogs/ShowcaseDialog";
import { PageEditDialog } from "@/components/admin/dialogs/PageEditDialog";
import { DropdownOptionsDialog } from "@/components/admin/dialogs/DropdownOptionsDialog";
import { ApiKeyEditDialog } from "@/components/admin/dialogs/ApiKeyEditDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SUBJECTS = ["Mathematics", "English Language", "Integrated Science", "Social Studies", "Computing", "RME", "Creative Arts", "French", "Ghanaian Language"];
const DEFAULT_CLASS_LEVELS = ["Basic 7", "Basic 8", "Basic 9"];

const initialProductForm = {
  title: "", slug: "", subject: "", class_level: "",
  price_ghs: "", compare_at_price_ghs: "", description: "", short_description: "",
  features: [], is_digital: true, is_bundle: false, bundle_type: "",
  cover_image_url: "", page_count: "", question_count: "", duration_minutes: "", is_active: true, is_featured: false,
  target_roles: [], file_path: "", file_size_mb: ""
};

const initialPromoForm = {
  code: "", discount_type: "percentage", discount_value: "",
  min_order_amount: "", max_uses: "", valid_until: "",
  first_order_only: false, is_active: true
};

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, isAdmin, isSuperAdmin, refreshSessionTimeout } = useAuth();

  // Permissions
  const [myPermissions, setMyPermissions] = useState([]);
  const [myViewOnlyPermissions, setMyViewOnlyPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // Sub-Admin
  const [subAdmins, setSubAdmins] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [subAdminDialogOpen, setSubAdminDialogOpen] = useState(false);
  const [editingSubAdmin, setEditingSubAdmin] = useState(null);
  const [subAdminForm, setSubAdminForm] = useState({ email: "", name: "", password: "", permissions: [], view_only_permissions: [], access_code: "" });

  // Products
  const [products, setProducts] = useState([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(initialProductForm);

  // Promo codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoDialogOpen, setPromoDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState(initialPromoForm);

  // Banners
  const [banners, setBanners] = useState([]);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/mock-exams",
    bg_color: "#dc2626", text_color: "#ffffff", is_active: true, starts_at: "", expires_at: ""
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // Showcase
  const [showcaseProducts, setShowcaseProducts] = useState([]);
  const [showcaseDialogOpen, setShowcaseDialogOpen] = useState(false);
  const [editingShowcase, setEditingShowcase] = useState(null);
  const [showcaseForm, setShowcaseForm] = useState({
    title: "", description: "", price: "", image_url: "", category: "Books", is_published: true
  });
  const [showcaseSettings, setShowcaseSettings] = useState({ show_views: true, show_likes: true });
  const [showcaseInquiries, setShowcaseInquiries] = useState([]);

  // Site settings
  const [siteSettings, setSiteSettings] = useState({
    social_links: { facebook: "", tiktok: "", instagram: "", whatsapp: "" },
    pages: {
      help_center: { title: "Help Center", content: "" },
      contact_us: { title: "Contact Us", content: "" },
      refund_policy: { title: "Refund Policy", content: "" },
    },
  });
  const [editingPage, setEditingPage] = useState(null);

  // Analytics
  const [analytics, setAnalytics] = useState(null);

  // Subscribers
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);

  // Orders
  const [orders, setOrders] = useState([]);
  const [orderTotals, setOrderTotals] = useState({ completed: 0, pending: 0, failed: 0, revenue: 0 });
  const [orderFilter, setOrderFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Dashboard
  const [dashboard, setDashboard] = useState(null);

  // Dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({ subjects: DEFAULT_SUBJECTS, class_levels: DEFAULT_CLASS_LEVELS });
  const [editingDropdown, setEditingDropdown] = useState(null);
  const [newOptionValue, setNewOptionValue] = useState("");

  // API Keys
  const [apiKeys, setApiKeys] = useState(null);
  const [apiKeysTesting, setApiKeysTesting] = useState({});
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [apiKeyForm, setApiKeyForm] = useState({});

  // User Sessions
  const [userSessions, setUserSessions] = useState(null);
  const [sessionTimeoutSetting, setSessionTimeoutSetting] = useState(30);

  // Admin Notification
  const [notificationSettings, setNotificationSettings] = useState(null);

  // Sales Summary
  const [salesSummarySettings, setSalesSummarySettings] = useState(null);
  const [salesSummaryPreview, setSalesSummaryPreview] = useState(null);

  // Theme
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("admin-theme") === "dark");

  // Activity Log
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityFilterEmail, setActivityFilterEmail] = useState("");
  const [activityFilterAction, setActivityFilterAction] = useState("");
  const [activityFilterOptions, setActivityFilterOptions] = useState({ emails: [], actions: [] });

  // Analytics
  const [signupAnalytics, setSignupAnalytics] = useState(null);
  const [roleAnalytics, setRoleAnalytics] = useState(null);

  // Admin search
  const [adminSearch, setAdminSearch] = useState("");

  // ─── Auth Guard ───
  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      toast.error("Admin access required");
      navigate("/login", { state: { from: { pathname: "/admin" } } });
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user && isAdmin) fetchMyPermissions();
  }, [user, isAdmin]);

  useEffect(() => {
    if (user && isAdmin && permissionsLoaded) {
      if (hasPerm("manage_products")) fetchProducts();
      if (hasPerm("manage_promo_codes")) fetchPromoCodes();
      if (hasPerm("manage_banners")) fetchBanners();
      if (hasPerm("manage_showcase")) fetchShowcaseProducts();
      if (hasPerm("manage_site_settings")) fetchSiteSettings();
      if (hasPerm("view_analytics")) fetchAnalytics();
      if (hasPerm("manage_subscribers")) fetchSubscribers();
      if (hasPerm("manage_orders")) fetchOrders();
      if (hasPerm("manage_products")) fetchDropdownOptions();
      if (hasPerm("manage_api_keys")) fetchApiKeys();
      if (hasPerm("manage_users")) { fetchUserSessions(); fetchSessionTimeout(); fetchSignupAnalytics(); fetchRoleAnalytics(); }
      fetchDashboard();
      fetchNotificationSettings();
      fetchSalesSummarySettings();
      fetchActivityLog();
      if (isSuperAdmin) fetchSubAdmins();
    }
  }, [user, isAdmin, permissionsLoaded]);

  const hasPerm = (perm) => isSuperAdmin || myPermissions.includes(perm);
  const isViewOnly = (perm) => !isSuperAdmin && myViewOnlyPermissions.includes(perm);

  // ─── Fetch Functions ───
  const fetchMyPermissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/my-permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setMyPermissions(response.data.permissions || []);
      setMyViewOnlyPermissions(response.data.view_only_permissions || []);
      setAllPermissions(response.data.all_permissions || []);
      const codeStatus = response.data.access_code_status;
      if (codeStatus && codeStatus.needs_verification) {
        navigate("/admin/access-code", { replace: true });
        return;
      }
    } catch (error) { console.error("Error fetching permissions:", error); }
    finally { setPermissionsLoaded(true); }
  };

  const fetchSubAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/sub-admins`, { headers: { Authorization: `Bearer ${token}` } });
      setSubAdmins(response.data.sub_admins || []);
      if (response.data.all_permissions) setAllPermissions(response.data.all_permissions);
    } catch (error) { console.error("Error fetching sub-admins:", error); }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(response.data.products);
    } catch (error) { console.error("Error fetching products:", error); }
    finally { setLoading(false); }
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, { headers: { Authorization: `Bearer ${token}` } });
      setPromoCodes(response.data);
    } catch (error) { console.error("Error fetching promo codes:", error); }
  };

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/admin/banners`, { headers: { Authorization: `Bearer ${token}` } });
      setBanners(response.data);
    } catch (error) { console.error("Error fetching banners:", error); }
  };

  const fetchShowcaseProducts = async () => {
    try {
      const [prodRes, settingsRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/admin/showcase/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/showcase/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/showcase/inquiries`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setShowcaseProducts(prodRes.data);
      setShowcaseSettings(settingsRes.data);
      setShowcaseInquiries(inquiriesRes.data);
    } catch (e) { console.error(e); }
  };

  const fetchSiteSettings = async () => {
    try { const res = await axios.get(`${API}/site-settings`); setSiteSettings(res.data); } catch (e) { console.error(e); }
  };

  const fetchAnalytics = async () => {
    try { const res = await axios.get(`${API}/admin/showcase/analytics`, { headers: { Authorization: `Bearer ${token}` } }); setAnalytics(res.data); } catch (e) { console.error(e); }
  };

  const fetchSubscribers = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/subscribers`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscribers(res.data.subscribers); setSubscriberCount(res.data.total);
    } catch (e) { console.error(e); }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(res.data.orders); setOrderTotals(res.data.totals);
    } catch (e) { console.error(e); }
  };

  const fetchDashboard = async () => {
    try { const res = await axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }); setDashboard(res.data); } catch (e) { console.error(e); }
  };

  const fetchDropdownOptions = async () => {
    try {
      const res = await axios.get(`${API}/admin/dropdown-options`);
      setDropdownOptions({ subjects: res.data.subjects, class_levels: res.data.class_levels });
    } catch (e) { console.error(e); }
  };

  const fetchApiKeys = async () => {
    try { const res = await axios.get(`${API}/admin/api-keys`, { headers: { Authorization: `Bearer ${token}` } }); setApiKeys(res.data); } catch (e) { console.error(e); }
  };

  const fetchUserSessions = async () => {
    try { const res = await axios.get(`${API}/admin/users/sessions`, { headers: { Authorization: `Bearer ${token}` } }); setUserSessions(res.data); } catch (e) { console.error(e); }
  };

  const fetchSessionTimeout = async () => {
    try { const res = await axios.get(`${API}/admin/session-settings`, { headers: { Authorization: `Bearer ${token}` } }); setSessionTimeoutSetting(res.data.timeout_minutes); } catch (e) { console.error(e); }
  };

  const fetchActivityLog = async (page = 1) => {
    try {
      let url = `${API}/admin/activity-log?page=${page}&per_page=30`;
      if (activityFilterEmail) url += `&user_email=${activityFilterEmail}`;
      if (activityFilterAction) url += `&action=${activityFilterAction}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setActivityLogs(response.data.logs || []); setActivityTotal(response.data.total || 0);
      setActivityPage(page); setActivityFilterOptions(response.data.filter_options || { emails: [], actions: [] });
    } catch (error) { console.error("Error fetching activity log:", error); }
  };

  const fetchSignupAnalytics = async () => {
    try { const response = await axios.get(`${API}/admin/analytics/signups`, { headers: { Authorization: `Bearer ${token}` } }); setSignupAnalytics(response.data); } catch (error) { console.error("Error fetching signup analytics:", error); }
  };

  const fetchRoleAnalytics = async () => {
    try { const response = await axios.get(`${API}/admin/analytics/user-roles`, { headers: { Authorization: `Bearer ${token}` } }); setRoleAnalytics(response.data); } catch (error) { console.error("Error fetching role analytics:", error); }
  };

  const fetchNotificationSettings = async () => {
    try { const res = await axios.get(`${API}/admin/notification-settings`, { headers: { Authorization: `Bearer ${token}` } }); setNotificationSettings(res.data); } catch (e) { console.error(e); }
  };

  const fetchSalesSummarySettings = async () => {
    try { const res = await axios.get(`${API}/admin/sales-summary-settings`, { headers: { Authorization: `Bearer ${token}` } }); setSalesSummarySettings(res.data); } catch (e) { console.error(e); }
  };

  // ─── Handlers ───
  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("admin-theme", newMode ? "dark" : "light");
  };

  const formatPrice = (price) => new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(price);
  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  // Sub-admin handlers
  const createSubAdmin = async () => {
    if (!subAdminForm.email || !subAdminForm.name || !subAdminForm.password) { toast.error("Email, name, and password are required"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/sub-admins`, { ...subAdminForm, access_code: subAdminForm.access_code || undefined }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sub-admin created! Access code: ${res.data.access_code}`);
      setSubAdminDialogOpen(false); setSubAdminForm({ email: "", name: "", password: "", permissions: [], view_only_permissions: [], access_code: "" }); fetchSubAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create sub-admin"); }
    finally { setSaving(false); }
  };

  const updateSubAdmin = async () => {
    if (!editingSubAdmin) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/sub-admins/${editingSubAdmin.id}`, { permissions: subAdminForm.permissions, view_only_permissions: subAdminForm.view_only_permissions, name: subAdminForm.name || undefined, access_code: subAdminForm.access_code || undefined }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Sub-admin permissions updated"); setEditingSubAdmin(null); setSubAdminDialogOpen(false);
      setSubAdminForm({ email: "", name: "", password: "", permissions: [], view_only_permissions: [], access_code: "" }); fetchSubAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update sub-admin"); }
    finally { setSaving(false); }
  };

  const removeSubAdmin = async (id) => {
    if (!window.confirm("Remove this sub-admin? They will be demoted to a regular user.")) return;
    try { await axios.delete(`${API}/admin/sub-admins/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Sub-admin removed"); fetchSubAdmins(); }
    catch (error) { toast.error(error.response?.data?.detail || "Failed to remove sub-admin"); }
  };

  // Product handlers
  const handleProductInputChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => { const updated = { ...prev, [name]: value }; if (name === 'title') updated.slug = generateSlug(value); return updated; });
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      title: product.title, slug: product.slug, subject: product.subject,
      class_level: product.class_level,
      price_ghs: product.price_ghs.toString(), compare_at_price_ghs: product.compare_at_price_ghs?.toString() || "",
      description: product.description, short_description: product.short_description || "",
      features: product.features || [], is_digital: product.is_digital,
      is_bundle: product.is_bundle, bundle_type: product.bundle_type || "",
      cover_image_url: product.cover_image_url || "", page_count: product.page_count?.toString() || "",
      question_count: product.question_count?.toString() || "", duration_minutes: product.duration_minutes?.toString() || "",
      is_active: product.is_active, is_featured: product.is_featured,
      target_roles: product.target_roles || [], file_path: product.file_path || "", file_size_mb: product.file_size_mb?.toString() || ""
    });
    setProductDialogOpen(true);
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        title: productForm.title, slug: productForm.slug, subject: productForm.subject,
        class_level: productForm.class_level,
        price_ghs: parseFloat(productForm.price_ghs), compare_at_price_ghs: productForm.compare_at_price_ghs ? parseFloat(productForm.compare_at_price_ghs) : null,
        description: productForm.description, short_description: productForm.short_description,
        features: Array.isArray(productForm.features) ? productForm.features.filter(f => f.trim()) : [],
        is_digital: productForm.is_digital, is_bundle: productForm.is_bundle,
        bundle_type: productForm.bundle_type || null, bundle_contains: [],
        cover_image_url: productForm.cover_image_url || null, preview_pages: [],
        page_count: productForm.page_count ? parseInt(productForm.page_count) : null,
        question_count: productForm.question_count ? parseInt(productForm.question_count) : null,
        duration_minutes: productForm.duration_minutes ? parseInt(productForm.duration_minutes) : null,
        is_active: productForm.is_active, is_featured: productForm.is_featured,
        target_roles: productForm.target_roles || [], sort_order: 0,
        file_path: productForm.file_path || null, file_size_mb: productForm.file_size_mb ? parseFloat(productForm.file_size_mb) : null
      };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Product created");
      }
      setProductDialogOpen(false); setEditingProduct(null); setProductForm(initialProductForm); fetchProducts();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save product"); }
    finally { setSaving(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm("Delete this product?")) return;
    try { await axios.delete(`${API}/admin/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Product deleted"); fetchProducts(); }
    catch (error) { toast.error("Failed to delete product"); }
  };

  // Promo code handlers
  const openEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({ code: promo.code, discount_type: promo.discount_type, discount_value: promo.discount_value.toString(), min_order_amount: promo.min_order_amount?.toString() || "", max_uses: promo.max_uses?.toString() || "", valid_until: promo.valid_until ? promo.valid_until.split('T')[0] : "", first_order_only: promo.first_order_only, is_active: promo.is_active });
    setPromoDialogOpen(true);
  };

  const handlePromoSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { code: promoForm.code.toUpperCase(), discount_type: promoForm.discount_type, discount_value: parseFloat(promoForm.discount_value), min_order_amount: promoForm.min_order_amount ? parseFloat(promoForm.min_order_amount) : null, max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null, valid_until: promoForm.valid_until ? new Date(promoForm.valid_until).toISOString() : null, first_order_only: promoForm.first_order_only, is_active: promoForm.is_active };
      if (editingPromo) { await axios.put(`${API}/admin/promo-codes/${editingPromo.id}`, payload, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Promo code updated"); }
      else { await axios.post(`${API}/admin/promo-codes`, payload, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Promo code created"); }
      setPromoDialogOpen(false); setEditingPromo(null); setPromoForm(initialPromoForm); fetchPromoCodes();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save promo code"); }
    finally { setSaving(false); }
  };

  const handleDeletePromo = async (promoId) => {
    if (!confirm("Delete this promo code?")) return;
    try { await axios.delete(`${API}/admin/promo-codes/${promoId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Promo code deleted"); fetchPromoCodes(); }
    catch (error) { toast.error("Failed to delete promo code"); }
  };

  const copyPromoCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Copied: ${code}`); };

  // Banner handlers
  const handleBannerSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editingBanner) { await axios.put(`${API}/admin/banners/${editingBanner.id}`, bannerForm, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Banner updated"); }
      else { await axios.post(`${API}/admin/banners`, bannerForm, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Banner created"); }
      setBannerDialogOpen(false); fetchBanners();
    } catch (error) { toast.error("Failed to save banner"); }
    finally { setSaving(false); }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try { await axios.delete(`${API}/admin/banners/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Banner deleted"); fetchBanners(); }
    catch (error) { toast.error("Failed to delete banner"); }
  };

  // Showcase handlers
  const handleShowcaseSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...showcaseForm, price: showcaseForm.price ? parseFloat(showcaseForm.price) : null };
    try {
      if (editingShowcase) { await axios.put(`${API}/admin/showcase/products/${editingShowcase.id}`, payload, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Product updated"); }
      else { await axios.post(`${API}/admin/showcase/products`, payload, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Product created"); }
      setShowcaseDialogOpen(false); fetchShowcaseProducts();
    } catch (e) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const deleteShowcase = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await axios.delete(`${API}/admin/showcase/products/${id}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Deleted"); fetchShowcaseProducts(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const updateShowcaseSettings = async (key, value) => {
    const updated = { ...showcaseSettings, [key]: value }; setShowcaseSettings(updated);
    try { await axios.put(`${API}/admin/showcase/settings`, updated, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Settings updated"); }
    catch (e) { toast.error("Failed to update settings"); }
  };

  // Site settings handlers
  const saveSiteSettings = async () => {
    setSaving(true);
    try { await axios.put(`${API}/admin/site-settings`, siteSettings, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Settings saved"); }
    catch (e) { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  // Dropdown options
  const saveDropdownOptions = async (updated) => {
    try { await axios.put(`${API}/admin/dropdown-options`, updated, { headers: { Authorization: `Bearer ${token}` } }); setDropdownOptions(updated); toast.success("Options updated"); }
    catch (e) { toast.error("Failed to update"); }
  };
  const addDropdownOption = (field) => {
    if (!newOptionValue.trim()) return;
    const current = [...(dropdownOptions[field] || [])];
    if (current.includes(newOptionValue.trim())) { toast.error("Already exists"); return; }
    current.push(newOptionValue.trim());
    saveDropdownOptions({ ...dropdownOptions, [field]: current }); setNewOptionValue("");
  };
  const removeDropdownOption = (field, value) => { saveDropdownOptions({ ...dropdownOptions, [field]: dropdownOptions[field].filter(v => v !== value) }); };

  // API Keys handlers
  const testApiKeyConnection = async (service) => {
    setApiKeysTesting(prev => ({ ...prev, [service]: true }));
    try {
      const res = await axios.post(`${API}/admin/api-keys/test/${service}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) toast.success(res.data.message); else toast.error(res.data.error || "Connection failed");
      fetchApiKeys();
    } catch (e) { toast.error("Test failed"); }
    finally { setApiKeysTesting(prev => ({ ...prev, [service]: false })); }
  };

  const updateApiKeys = async (service, credentials) => {
    setSaving(true);
    try { await axios.put(`${API}/admin/api-keys`, { service, credentials }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`${service.charAt(0).toUpperCase() + service.slice(1)} credentials updated`); setEditingApiKey(null); setApiKeyForm({}); fetchApiKeys(); }
    catch (e) { toast.error("Failed to update credentials"); } finally { setSaving(false); }
  };

  // Session timeout
  const updateSessionTimeout = async (minutes) => {
    setSaving(true);
    try { await axios.put(`${API}/admin/session-settings`, { timeout_minutes: minutes }, { headers: { Authorization: `Bearer ${token}` } }); setSessionTimeoutSetting(minutes); refreshSessionTimeout(); toast.success(minutes === 0 ? "Session timeout disabled" : `Session timeout set to ${minutes} minutes`); }
    catch (e) { toast.error("Failed to update session timeout"); } finally { setSaving(false); }
  };

  // Notification settings
  const updateNotificationSettings = async (updates) => {
    const updated = { ...notificationSettings, ...updates }; setNotificationSettings(updated);
    try { await axios.put(`${API}/admin/notification-settings`, updated, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Notification settings updated"); }
    catch (e) { toast.error("Failed to update notification settings"); fetchNotificationSettings(); }
  };

  const sendTestNotification = async () => {
    setSaving(true);
    try { await axios.post(`${API}/admin/test-notification`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Test notification sent (if Brevo is configured)"); }
    catch (e) { toast.error("Failed to send test notification"); } finally { setSaving(false); }
  };

  // Sales summary
  const updateSalesSummarySettings = async (updates) => {
    const updated = { ...salesSummarySettings, ...updates }; setSalesSummarySettings(updated);
    try { await axios.put(`${API}/admin/sales-summary-settings`, updated, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Sales summary settings updated"); }
    catch (e) { toast.error("Failed to update settings"); fetchSalesSummarySettings(); }
  };

  const sendSalesSummaryNow = async (period) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/sales-summary/send/${period}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) toast.success(`${period === 'daily' ? 'Daily' : 'Weekly'} summary sent!`); else toast.error(res.data.message || "Failed to send");
      fetchSalesSummarySettings();
    } catch (e) { toast.error("Failed to send summary"); } finally { setSaving(false); }
  };

  const previewSalesSummary = async (period) => {
    try { const res = await axios.get(`${API}/admin/sales-summary/preview/${period}`, { headers: { Authorization: `Bearer ${token}` } }); setSalesSummaryPreview(res.data); }
    catch (e) { toast.error("Failed to load preview"); }
  };

  // Orders filter
  const getFilteredOrders = () => {
    return orders.filter(o => {
      if (orderFilter !== "all" && o.status !== orderFilter) return false;
      if (dateRange.from) {
        const d = new Date(o.created_at);
        if (d < dateRange.from) return false;
        if (dateRange.to) { const end = new Date(dateRange.to); end.setHours(23, 59, 59, 999); if (d > end) return false; }
      }
      if (adminSearch) {
        const q = adminSearch.toLowerCase();
        if (!(o.transaction_ref || "").toLowerCase().includes(q) && !(o.customer_name || "").toLowerCase().includes(q) && !(o.customer_email || "").toLowerCase().includes(q)) return false;
      }
      return true;
    });
  };

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? "bg-gray-900 text-gray-100" : "bg-secondary/30"}`} data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className={`font-heading text-3xl md:text-4xl font-bold ${darkMode ? "text-white" : ""}`}>Admin Dashboard</h1>
            <p className={`mt-1 ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>Manage products, promo codes, and more</p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`relative flex items-center ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border rounded-xl h-10 px-3 w-48 md:w-64 transition-all focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary`}>
              <Search className="w-4 h-4 shrink-0 text-gray-400" />
              <input type="text" value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} placeholder="Search products, orders..."
                className={`ml-2 w-full bg-transparent outline-none text-sm ${darkMode ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"}`} data-testid="admin-search-input" />
              {adminSearch && (
                <button onClick={() => setAdminSearch("")} className="ml-1 shrink-0">
                  <X className={`w-3.5 h-3.5 ${darkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`} />
                </button>
              )}
            </div>
            <Button variant="outline" size="icon"
              className={`rounded-xl h-10 w-10 ${darkMode ? "bg-gray-800 border-gray-700 text-yellow-400 hover:bg-gray-700" : "bg-white hover:bg-gray-50"}`}
              onClick={toggleTheme} data-testid="theme-toggle-btn" title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button onClick={() => navigate("/admin/analytics")} className="rounded-xl bg-gradient-to-r from-primary to-primary/80 shadow-glow" data-testid="analytics-btn">
              <BarChart3 className="w-4 h-4 mr-2" /> View Analytics
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Package, label: "Products", value: products.length, color: "bg-primary/10 text-primary", darkColor: "bg-blue-900/30 text-blue-400" },
            { icon: Tag, label: "Promo Codes", value: promoCodes.length, color: "bg-accent/10 text-accent", darkColor: "bg-amber-900/30 text-amber-400" },
            { icon: ShoppingCart, label: "Orders", value: "-", color: "bg-success/10 text-success", darkColor: "bg-green-900/30 text-green-400" },
            { icon: Users, label: "Users", value: "-", color: "bg-blue-500/10 text-blue-500", darkColor: "bg-blue-900/30 text-blue-400" }
          ].map((stat, i) => (
            <Card key={i} className={`border-0 shadow-soft ${darkMode ? "bg-gray-800/80 border border-gray-700/50" : ""}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl ${darkMode ? stat.darkColor : stat.color} flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className={`text-2xl font-bold ${darkMode ? "text-white" : ""}`}>{stat.value}</p>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={`shadow-soft rounded-2xl p-1.5 mb-16 flex-wrap ${darkMode ? "bg-gray-800/80 border border-gray-700/50" : "bg-white"}`}>
            <TabsTrigger value="overview" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="overview-tab"><Activity className="w-4 h-4 mr-2" />Overview</TabsTrigger>
            {hasPerm("manage_products") && <TabsTrigger value="products" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><Package className="w-4 h-4 mr-2" />Products</TabsTrigger>}
            {hasPerm("manage_promo_codes") && <TabsTrigger value="promo-codes" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><Tag className="w-4 h-4 mr-2" />Promo Codes</TabsTrigger>}
            {hasPerm("manage_banners") && <TabsTrigger value="banners" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><Megaphone className="w-4 h-4 mr-2" />Banners</TabsTrigger>}
            {hasPerm("manage_showcase") && <TabsTrigger value="showcase" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><ShoppingBag className="w-4 h-4 mr-2" />Showcase</TabsTrigger>}
            {hasPerm("manage_site_settings") && <TabsTrigger value="site-settings" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><Settings className="w-4 h-4 mr-2" />Site Settings</TabsTrigger>}
            {hasPerm("view_analytics") && <TabsTrigger value="engagement" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><BarChart3 className="w-4 h-4 mr-2" />Engagement</TabsTrigger>}
            {hasPerm("manage_subscribers") && (
              <TabsTrigger value="subscribers" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 2H4a2 2 0 0 0-2 2v1" /><rect x="2" y="6" width="16" height="13" rx="2" /><polyline points="2,6 10,12 18,6" />
                  <circle cx="19" cy="19" r="4.5" fill="currentColor" stroke="none" /><line x1="19" y1="16.5" x2="19" y2="21.5" stroke="white" strokeWidth="2" /><line x1="16.5" y1="19" x2="21.5" y2="19" stroke="white" strokeWidth="2" />
                </svg>Subscribers
              </TabsTrigger>
            )}
            {hasPerm("manage_orders") && <TabsTrigger value="orders" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><DollarSign className="w-4 h-4 mr-2" />Orders</TabsTrigger>}
            {hasPerm("manage_users") && <TabsTrigger value="users" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="users-tab"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>}
            {hasPerm("manage_api_keys") && <TabsTrigger value="api-keys" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white"><Key className="w-4 h-4 mr-2" />API Keys</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="sub-admins" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="sub-admins-tab"><Shield className="w-4 h-4 mr-2" />Sub-Admins</TabsTrigger>}
            {isSuperAdmin && <TabsTrigger value="security" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="security-tab"><Lock className="w-4 h-4 mr-2" />Security</TabsTrigger>}
            <TabsTrigger value="activity-log" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="activity-log-tab"><ClipboardList className="w-4 h-4 mr-2" />Activity Log</TabsTrigger>
            <TabsTrigger value="customer-view" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="customer-view-tab-trigger"><Monitor className="w-4 h-4 mr-2" />Customer View</TabsTrigger>
            <TabsTrigger value="email-settings" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="email-settings-tab-trigger"><Mail className="w-4 h-4 mr-2" />Email Settings</TabsTrigger>
            <TabsTrigger value="sample-mocks" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="sample-mocks-tab-trigger"><BookOpen className="w-4 h-4 mr-2" />Sample Mocks</TabsTrigger>
            <TabsTrigger value="quiz-challenge" className="rounded-xl px-6 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="quiz-challenge-tab-trigger"><Trophy className="w-4 h-4 mr-2" />Quiz Challenge</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab dashboard={dashboard} darkMode={darkMode} SalesChart={SalesChart} /></TabsContent>
          <TabsContent value="products">
            <ProductsTab darkMode={darkMode} products={products} loading={loading} adminSearch={adminSearch} openEditProduct={openEditProduct} handleDeleteProduct={handleDeleteProduct}
              setEditingProduct={setEditingProduct} setProductForm={setProductForm} setProductDialogOpen={setProductDialogOpen} initialProductForm={initialProductForm} formatPrice={formatPrice} viewOnly={isViewOnly("manage_products")} />
          </TabsContent>
          <TabsContent value="promo-codes">
            <PromoCodesTab darkMode={darkMode} promoCodes={promoCodes} adminSearch={adminSearch} formatPrice={formatPrice} openEditPromo={openEditPromo}
              handleDeletePromo={handleDeletePromo} copyPromoCode={copyPromoCode} setEditingPromo={setEditingPromo} setPromoForm={setPromoForm}
              setPromoDialogOpen={setPromoDialogOpen} initialPromoForm={initialPromoForm} viewOnly={isViewOnly("manage_promo_codes")} />
          </TabsContent>
          <TabsContent value="banners">
            <BannersTab darkMode={darkMode} banners={banners} deleteBanner={deleteBanner} setEditingBanner={setEditingBanner} setBannerForm={setBannerForm} setBannerDialogOpen={setBannerDialogOpen} viewOnly={isViewOnly("manage_banners")} />
          </TabsContent>
          <TabsContent value="showcase">
            <ShowcaseTab darkMode={darkMode} showcaseProducts={showcaseProducts} showcaseInquiries={showcaseInquiries} showcaseSettings={showcaseSettings}
              updateShowcaseSettings={updateShowcaseSettings} deleteShowcase={deleteShowcase} setEditingShowcase={setEditingShowcase} setShowcaseForm={setShowcaseForm}
              setShowcaseDialogOpen={setShowcaseDialogOpen} viewOnly={isViewOnly("manage_showcase")} />
          </TabsContent>
          <TabsContent value="site-settings">
            <SiteSettingsTab darkMode={darkMode} siteSettings={siteSettings} setSiteSettings={setSiteSettings} saveSiteSettings={saveSiteSettings} saving={saving}
              editingPage={editingPage} setEditingPage={setEditingPage} viewOnly={isViewOnly("manage_site_settings")} />
          </TabsContent>
          <TabsContent value="engagement"><EngagementTab darkMode={darkMode} analytics={analytics} /></TabsContent>
          <TabsContent value="subscribers">
            <SubscribersTab darkMode={darkMode} subscribers={subscribers} subscriberCount={subscriberCount} adminSearch={adminSearch} viewOnly={isViewOnly("manage_subscribers")} />
          </TabsContent>
          <TabsContent value="orders">
            <OrdersTab darkMode={darkMode} orders={orders} orderFilter={orderFilter} setOrderFilter={setOrderFilter} orderTotals={orderTotals}
              getFilteredOrders={getFilteredOrders} dateRange={dateRange} setDateRange={setDateRange} datePickerOpen={datePickerOpen} setDatePickerOpen={setDatePickerOpen} viewOnly={isViewOnly("manage_orders")} token={token} />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab darkMode={darkMode} userSessions={userSessions} fetchUserSessions={fetchUserSessions} sessionTimeoutSetting={sessionTimeoutSetting}
              updateSessionTimeout={updateSessionTimeout} saving={saving} viewOnly={isViewOnly("manage_users")}
              signupAnalytics={signupAnalytics} roleAnalytics={roleAnalytics} />
          </TabsContent>
          <TabsContent value="api-keys">
            <ApiKeysTab darkMode={darkMode} apiKeys={apiKeys} apiKeysTesting={apiKeysTesting} saving={saving} testApiKeyConnection={testApiKeyConnection}
              setEditingApiKey={setEditingApiKey} setApiKeyForm={setApiKeyForm} notificationSettings={notificationSettings} setNotificationSettings={setNotificationSettings}
              updateNotificationSettings={updateNotificationSettings} sendTestNotification={sendTestNotification} salesSummarySettings={salesSummarySettings}
              updateSalesSummarySettings={updateSalesSummarySettings} previewSalesSummary={previewSalesSummary} sendSalesSummaryNow={sendSalesSummaryNow}
              salesSummaryPreview={salesSummaryPreview} setSalesSummaryPreview={setSalesSummaryPreview} viewOnly={isViewOnly("manage_api_keys")} />
          </TabsContent>
          {isSuperAdmin && <TabsContent value="sub-admins" data-testid="sub-admins-content">
            <SubAdminsTab darkMode={darkMode} subAdmins={subAdmins} removeSubAdmin={removeSubAdmin} setEditingSubAdmin={setEditingSubAdmin} setSubAdminForm={setSubAdminForm} setSubAdminDialogOpen={setSubAdminDialogOpen} />
          </TabsContent>}
          {isSuperAdmin && <TabsContent value="security" data-testid="security-content">
            <SecurityTab darkMode={darkMode} token={token} subAdmins={subAdmins} fetchSubAdmins={fetchSubAdmins} />
          </TabsContent>}
          <TabsContent value="activity-log" data-testid="activity-log-content">
            <ActivityLogTab darkMode={darkMode} activityLogs={activityLogs} activityTotal={activityTotal} activityPage={activityPage} fetchActivityLog={fetchActivityLog}
              isSuperAdmin={isSuperAdmin} activityFilterEmail={activityFilterEmail} setActivityFilterEmail={setActivityFilterEmail}
              activityFilterAction={activityFilterAction} setActivityFilterAction={setActivityFilterAction} activityFilterOptions={activityFilterOptions} />
          </TabsContent>
          <TabsContent value="customer-view" data-testid="customer-view-content"><CustomerViewTab darkMode={darkMode} viewOnly={isViewOnly("manage_site_settings")} /></TabsContent>
          <TabsContent value="email-settings" data-testid="email-settings-content"><EmailSettingsTab darkMode={darkMode} viewOnly={isViewOnly("manage_site_settings")} /></TabsContent>
          <TabsContent value="sample-mocks" data-testid="sample-mocks-content">
            <div className="space-y-8"><SampleMocksManager darkMode={darkMode} /><QuizAnalytics darkMode={darkMode} /></div>
          </TabsContent>
          <TabsContent value="quiz-challenge" data-testid="quiz-challenge-content">
            <QuizChallengeTab darkMode={darkMode} />
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        <SubAdminDialog
          open={subAdminDialogOpen}
          onOpenChange={(v) => { if (!v) { setSubAdminDialogOpen(false); setEditingSubAdmin(null); } else setSubAdminDialogOpen(true); }}
          editingSubAdmin={editingSubAdmin} subAdminForm={subAdminForm} setSubAdminForm={setSubAdminForm}
          allPermissions={allPermissions} saving={saving} onSave={editingSubAdmin ? updateSubAdmin : createSubAdmin}
        />
        <PageEditDialog editingPage={editingPage} setEditingPage={setEditingPage} siteSettings={siteSettings} setSiteSettings={setSiteSettings} saveSiteSettings={saveSiteSettings} saving={saving} />
        <ShowcaseDialog open={showcaseDialogOpen} onOpenChange={setShowcaseDialogOpen} editingShowcase={editingShowcase} showcaseForm={showcaseForm} setShowcaseForm={setShowcaseForm} handleShowcaseSubmit={handleShowcaseSubmit} saving={saving} />
        <BannerDialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen} editingBanner={editingBanner} bannerForm={bannerForm} setBannerForm={setBannerForm} handleBannerSubmit={handleBannerSubmit} saving={saving} />
        <ProductDialog open={productDialogOpen} onOpenChange={setProductDialogOpen} editingProduct={editingProduct} productForm={productForm} setProductForm={setProductForm}
          handleProductInputChange={handleProductInputChange} handleProductSubmit={handleProductSubmit} saving={saving} dropdownOptions={dropdownOptions} setEditingDropdown={setEditingDropdown} />
        <PromoCodeDialog open={promoDialogOpen} onOpenChange={setPromoDialogOpen} editingPromo={editingPromo} promoForm={promoForm} setPromoForm={setPromoForm} handlePromoSubmit={handlePromoSubmit} saving={saving} />
        <DropdownOptionsDialog editingDropdown={editingDropdown} setEditingDropdown={setEditingDropdown} dropdownOptions={dropdownOptions}
          newOptionValue={newOptionValue} setNewOptionValue={setNewOptionValue} addDropdownOption={addDropdownOption} removeDropdownOption={removeDropdownOption} />
        <ApiKeyEditDialog editingApiKey={editingApiKey} setEditingApiKey={setEditingApiKey} apiKeyForm={apiKeyForm} setApiKeyForm={setApiKeyForm} updateApiKeys={updateApiKeys} saving={saving} />
      </div>
    </div>
  );
}
